import { ComponentProps } from "react";
import { useSetPageParam } from "@hooks/useSetPageParam";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { cn } from "@util/helpers";

type PodcastsListingStoriesProps = {
  /** The card prop to be displayed. */
  cardProp: ComponentProps<typeof Card>;
  /** The index of the card in the listing. */
  index: number;
};

/**
 * Component to render individual podcast listing stories.
 */
const PodcastsListingStories = ({
  cardProp,
  index,
}: PodcastsListingStoriesProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const { ref } = useSetPageParam({ cardIndex: index, pageSize: 5 });

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-1/4",
      position: "right",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-3/4",
      extraClass: "pr-4",
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: "text-lg",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
  };

  return (
    <Card
      className="mb-4 border-gray-175 pt-4 [&:not(:first-child)]:border-t"
      {...cardProp}
      variations={variant}
      blurb={isScreenLG ? cardProp.blurb : undefined}
      ref={ref}
    />
  );
};

export default PodcastsListingStories;
